import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabaseClient";
import { Bell, ChevronDown, ChevronUp, Copy, Download, Fish, ImageIcon, LogOut, Pencil, Plus, Trash2, Upload, Users, X } from "lucide-react";
import { BOAT, SHORE, parseLbsOz, formatLbs } from "./cfsa-data";
import { loadRecordIndexes, checkAgainstRecord, applyRecordUpdatesToIndexes, evaluateRecordChecks } from "./recordsCheck";
import { formatKgAsLbsOz } from "./lib/recordMatching";
import { normalizeName } from "./lib/nameMatching";
import PageMeta from "./PageMeta";
import { canEnterRayBassHunt } from "./rayBassHunt";

// Split a kg weight into lbs / oz / drms strings, rounded to the nearest dram.
function splitKgToLbsOzDrms(kg) {
  const totalDrms = Math.round(kg * 2.20462 * 256);
  return {
    lbs: String(Math.floor(totalDrms / 256)),
    oz: String(Math.floor((totalDrms % 256) / 16)),
    drms: String(totalDrms % 16),
  };
}

function formatKgLbsOz(kg) {
  return formatLbs(kg * 2.20462);
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function csvEscape(value) {
  const str = String(value ?? "");
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function catchMonthKey(c) {
  const dateStr = c.weigh_date || c.created_at;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function downloadCatchesForMonthCsv(catches, monthKey) {
  const rows = catches
    .filter((c) => catchMonthKey(c) === monthKey)
    .slice()
    .sort((a, b) => (a.weigh_date || "").localeCompare(b.weigh_date || ""))
    .map((c) => [
      c.weigh_date ? formatDate(c.weigh_date) : "",
      c.angler_name,
      c.species,
      c.location,
      c.beach_location || "",
      formatKgLbsOz(c.weight_kg),
      c.is_catch_and_release ? "Yes" : "No",
    ]);
  const header = ["Date", "Angler", "Species", "Location", "Shore Location", "Weight", "Catch & Release"];
  const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fish-logged-${monthKey}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ConfirmDialog({ open, title, message, confirmLabel = "Confirm", loading, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-6 shadow-xl">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <p className="mt-2 text-sm text-slate-300">{message}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-400 hover:text-white transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-cyan-400 disabled:opacity-50 transition"
          >
            {loading ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordBadge({ check }) {
  if (!check || check.length === 0) return null;
  const hasBothScopes = check.length > 1;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {check.map(({ previous, recordType, scope }) => {
        const isOfficial = recordType === "official";
        const scopeLabel = hasBothScopes ? (scope === "junior" ? "Junior " : "Senior ") : "";
        const label = `New ${scopeLabel}${isOfficial ? "Official" : "C&R"} Record`;
        const title = previous.kg == null
          ? `No previous ${isOfficial ? "official" : "C&R"} record for this species/location/category`
          : `Beats ${previous.captor || "previous holder"} — ${formatKgAsLbsOz(previous.kg)} (${previous.year || "?"})`;
        return (
          <span
            key={scope}
            title={title}
            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-300 bg-amber-400/10 border border-amber-400/20"
          >
            🏆 {label}
          </span>
        );
      })}
    </div>
  );
}

// ── Add catch form ────────────────────────────────────────────────────────────
function AddCatchForm({ members, onAdded, recordIndexes }) {
  const [anglerName, setAnglerName] = useState("");
  const [location, setLocation] = useState("shore");
  const [beachLocation, setBeachLocation] = useState("");
  const [isArtificialLure, setIsArtificialLure] = useState(false);
  const [species, setSpecies] = useState("");
  const [metric, setMetric] = useState(false);
  const [lbs, setLbs] = useState("");
  const [oz, setOz] = useState("0");
  const [drms, setDrms] = useState("0");
  const [kg, setKg] = useState("");
  const [g, setG] = useState("0");
  const [weighDate, setWeighDate] = useState(new Date().toISOString().slice(0, 10));
  const [isJunior, setIsJunior] = useState(false);
  const [isFemale, setIsFemale] = useState(false);
  const [isClubTrip, setIsClubTrip] = useState(false);
  const [isCatchAndRelease, setIsCatchAndRelease] = useState(true);
  const [inRayBassHunt, setInRayBassHunt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const speciesData = location === "boat" ? BOAT : SHORE;

  useEffect(() => {
    if (species && !speciesData.find((r) => r.species === species)) setSpecies("");
  }, [location, species, speciesData]);

  useEffect(() => {
    if (location !== "shore") {
      setBeachLocation("");
    }
  }, [location]);

  const showRayBassHunt = canEnterRayBassHunt(location, weighDate);
  useEffect(() => {
    if (!showRayBassHunt) setInRayBassHunt(false);
  }, [showRayBassHunt]);

  const record = speciesData.find((r) => r.species === species);
  const specimenLbs = record ? parseLbsOz(record.sLbs) : null;

  function weightKg() {
    if (metric) {
      return (parseFloat(kg) || 0) + (parseInt(g) || 0) / 1000;
    }
    const decLbs = (parseFloat(lbs) || 0) + (parseInt(oz) || 0) / 16 + (parseInt(drms) || 0) / 256;
    return decLbs / 2.20462;
  }

  const wKg = weightKg();
  const pct = specimenLbs && wKg > 0 ? (wKg * 2.20462 / specimenLbs) * 100 : null;
  const canSubmit = anglerName.trim() && species && wKg > 0;
  const recordCheck = species && wKg > 0
    ? checkAgainstRecord({ species, location, weight_kg: wKg, is_junior: isJunior, is_catch_and_release: isCatchAndRelease }, recordIndexes)
    : null;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setShowConfirm(true);
  }

  async function confirmAdd() {
    setLoading(true);
    setError(null);
    const payload = {
      angler_name: anglerName.trim(),
      location,
      beach_location: beachLocation || null,
      is_artificial_lure: isArtificialLure,
      species,
      weight_kg: wKg,
      weigh_date: weighDate,
      is_junior: isJunior,
      is_female: isFemale,
      is_club_trip: isClubTrip,
      is_catch_and_release: isCatchAndRelease,
      in_ray_bass_hunt: inRayBassHunt,
    };
    let { error: err } = await supabase.from("catches").insert([payload]);
    if (err && /column .* does not exist|does not exist|not found/i.test(err.message)) {
      const fallbackPayload = { ...payload };
      delete fallbackPayload.is_catch_and_release;
      delete fallbackPayload.in_ray_bass_hunt;
      ({ error: err } = await supabase.from("catches").insert([fallbackPayload]));
    }
    if (err) { setError(err.message); }
    else {
      setAnglerName(""); setSpecies("");
      setLbs(""); setOz("0"); setDrms("0");
      setKg(""); setG("0");
      setBeachLocation("");
      setIsArtificialLure(false);
      setWeighDate(new Date().toISOString().slice(0, 10));
      setIsJunior(false);
      setIsFemale(false);
      setIsClubTrip(false);
      setIsCatchAndRelease(true);
      setInRayBassHunt(false);
      setShowConfirm(false);
      onAdded();
    }
    setLoading(false);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-400">Angler Name</label>
        <input
          list="catch-members-list"
          value={anglerName}
          onChange={(e) => setAnglerName(e.target.value)}
          required
          placeholder="Select or type angler name"
          className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
        />
        <datalist id="catch-members-list">
          {members.map((m) => <option key={m.id} value={m.name} />)}
        </datalist>
      </div>

      <div>
        <label className="mb-2 block text-xs font-medium text-slate-400">Angler Details</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isJunior}
              onChange={(e) => setIsJunior(e.target.checked)}
              className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
            />
            <span className="text-sm text-slate-300">Junior Member</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isFemale}
              onChange={(e) => setIsFemale(e.target.checked)}
              className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
            />
            <span className="text-sm text-slate-300">Female Angler</span>
          </label>
        </div>
      </div>

      <div>
        <label className="mb-2 block text-xs font-medium text-slate-400">Location</label>
        <div className="flex gap-3">
          {["shore", "boat"].map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => setLocation(loc)}
              className={`rounded-xl px-5 py-2 text-sm font-semibold capitalize transition ${location === loc ? "bg-cyan-500 text-slate-950" : "border border-white/10 text-slate-400 hover:text-white"}`}
            >
              {loc}
            </button>
          ))}
        </div>
      </div>

      {location === "shore" && (
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-400">Beach Location</label>
            <select
              value={beachLocation}
              onChange={(e) => setBeachLocation(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
            >
              <option value="">None</option>
              <option value="Praa Sands Beach">Praa Sands Beach</option>
              <option value="North Coast Beach">North Coast Beach</option>
            </select>
          </div>
        </div>
      )}

      {location === "boat" && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isClubTrip}
            onChange={(e) => setIsClubTrip(e.target.checked)}
            className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
          />
          <span className="text-sm text-slate-300">Club Trip</span>
        </label>
      )}

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={isCatchAndRelease}
          onChange={(e) => setIsCatchAndRelease(e.target.checked)}
          className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
        />
        <span className="text-sm text-slate-300">Catch &amp; Release</span>
      </label>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={isArtificialLure}
          onChange={(e) => setIsArtificialLure(e.target.checked)}
          className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
        />
        <span className="text-sm text-slate-300">Artificial lure used</span>
      </label>

      {showRayBassHunt && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={inRayBassHunt}
            onChange={(e) => setInRayBassHunt(e.target.checked)}
            className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
          />
          <span className="text-sm text-slate-300">Entered into Ray and Bass Hunt</span>
        </label>
      )}

      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-400">Species</label>
        <select
          value={species}
          onChange={(e) => setSpecies(e.target.value)}
          required
          className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
        >
          <option value="">Select species…</option>
          {speciesData.map((r) => <option key={r.species} value={r.species}>{r.species}</option>)}
        </select>
        {specimenLbs && (
          <p className="mt-1 text-xs text-slate-500">CFSA specimen: {formatLbs(specimenLbs)}</p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-medium text-slate-400">Weight</label>
          <button
            type="button"
            onClick={() => setMetric((m) => !m)}
            className="text-xs text-cyan-400 hover:text-cyan-300 transition"
          >
            Switch to {metric ? "Imperial (lbs/oz)" : "Metric (kg/g)"}
          </button>
        </div>
        {metric ? (
          <div className="flex gap-2">
            <div className="flex-1">
              <input type="number" min="0" value={kg} onChange={(e) => setKg(e.target.value)} placeholder="0" className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30" />
              <span className="mt-0.5 block text-center text-xs text-slate-500">kg</span>
            </div>
            <div className="w-24">
              <input type="number" min="0" max="999" value={g} onChange={(e) => setG(e.target.value)} placeholder="0" className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30" />
              <span className="mt-0.5 block text-center text-xs text-slate-500">g</span>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <div className="flex-1">
              <input type="number" min="0" value={lbs} onChange={(e) => setLbs(e.target.value)} placeholder="0" className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30" />
              <span className="mt-0.5 block text-center text-xs text-slate-500">lbs</span>
            </div>
            <div className="w-20">
              <select value={oz} onChange={(e) => setOz(e.target.value)} className="w-full rounded-xl border border-white/10 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60">
                {Array.from({ length: 16 }, (_, i) => <option key={i} value={i}>{i}</option>)}
              </select>
              <span className="mt-0.5 block text-center text-xs text-slate-500">oz</span>
            </div>
            <div className="w-20">
              <select value={drms} onChange={(e) => setDrms(e.target.value)} className="w-full rounded-xl border border-white/10 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60">
                {Array.from({ length: 16 }, (_, i) => <option key={i} value={i}>{i}</option>)}
              </select>
              <span className="mt-0.5 block text-center text-xs text-slate-500">drms</span>
            </div>
          </div>
        )}
        {pct !== null && (
          <p className={`mt-2 text-sm font-semibold ${pct >= 100 ? "text-cyan-300" : pct >= 75 ? "text-emerald-300" : "text-amber-300"}`}>
            {pct.toFixed(1)}% of CFSA specimen
          </p>
        )}
        {recordCheck && (
          <div className="mt-2">
            <RecordBadge check={recordCheck} />
          </div>
        )}
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-400">Weigh Date</label>
        <input
          type="date"
          value={weighDate}
          onChange={(e) => setWeighDate(e.target.value)}
          required
          className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
        />
      </div>

      {error && <p className="rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">{error}</p>}
      <button
        type="submit"
        disabled={!canSubmit || loading}
        className="flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
        {loading ? "Saving…" : "Log Catch"}
      </button>

      <ConfirmDialog
        open={showConfirm}
        title="Confirm catch"
        message={`Log a ${species} weighing ${formatKgLbsOz(wKg)} for ${anglerName.trim()}${isCatchAndRelease ? " as Catch & Release" : " as not Catch & Release"}?`}
        confirmLabel="Confirm & Save"
        loading={loading}
        onConfirm={confirmAdd}
        onCancel={() => setShowConfirm(false)}
      />
    </form>
  );
}

// ── Catch log table ───────────────────────────────────────────────────────────
function CatchRow({ c, members, onDelete, onSaved, isLast, recordCheck }) {
  const [editing, setEditing] = useState(false);
  const [anglerName, setAnglerName] = useState(c.angler_name);
  const [location, setLocation] = useState(c.location);
  const [beachLocation, setBeachLocation] = useState(c.beach_location || "");
  const [isArtificialLure, setIsArtificialLure] = useState(c.is_artificial_lure || false);
  const [species, setSpecies] = useState(c.species);
  const initialWeight = splitKgToLbsOzDrms(c.weight_kg);
  const [lbs, setLbs] = useState(initialWeight.lbs);
  const [oz, setOz] = useState(initialWeight.oz);
  const [drms, setDrms] = useState(initialWeight.drms);
  const [weighDate, setWeighDate] = useState(c.weigh_date ?? "");
  const [isJunior, setIsJunior] = useState(c.is_junior || false);
  const [isFemale, setIsFemale] = useState(c.is_female || false);
  const [isCatchAndRelease, setIsCatchAndRelease] = useState(c.is_catch_and_release ?? true);
  const [inRayBassHunt, setInRayBassHunt] = useState(c.in_ray_bass_hunt || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const speciesData = location === "boat" ? BOAT : SHORE;

  useEffect(() => {
    if (species && !speciesData.find((r) => r.species === species)) setSpecies("");
  }, [location, species, speciesData]);

  useEffect(() => {
    if (location !== "shore") {
      setBeachLocation("");
    }
  }, [location]);

  const showRayBassHunt = canEnterRayBassHunt(location, weighDate);
  useEffect(() => {
    if (!showRayBassHunt) setInRayBassHunt(false);
  }, [showRayBassHunt]);

  const record = speciesData.find((r) => r.species === species);
  const specimenLbs = record ? parseLbsOz(record.sLbs) : null;
  const editWeightLbs = (parseFloat(lbs) || 0) + (parseInt(oz) || 0) / 16 + (parseInt(drms) || 0) / 256;
  const editPct = specimenLbs && editWeightLbs > 0 ? (editWeightLbs / specimenLbs) * 100 : null;

  const data = c.location === "boat" ? BOAT : SHORE;
  const viewRecord = data.find((r) => r.species === c.species);
  const viewSpecimenLbs = viewRecord ? parseLbsOz(viewRecord.sLbs) : null;
  const viewPct = viewSpecimenLbs ? (c.weight_kg * 2.20462 / viewSpecimenLbs) * 100 : null;

  function cancelEdit() {
    setAnglerName(c.angler_name);
    setLocation(c.location);
    setBeachLocation(c.beach_location || "");
    setIsArtificialLure(c.is_artificial_lure || false);
    setSpecies(c.species);
    const w = splitKgToLbsOzDrms(c.weight_kg);
    setLbs(w.lbs);
    setOz(w.oz);
    setDrms(w.drms);
    setWeighDate(c.weigh_date ?? "");
    setIsJunior(c.is_junior || false);
    setIsFemale(c.is_female || false);
    setIsCatchAndRelease(c.is_catch_and_release ?? true);
    setInRayBassHunt(c.in_ray_bass_hunt || false);
    setError(null);
    setEditing(false);
  }

  function requestSaveEdit() {
    if (!anglerName.trim() || !species || editWeightLbs <= 0) return;
    setShowConfirm(true);
  }

  async function saveEdit() {
    setSaving(true);
    setError(null);
    // Keep the stored weight untouched unless the weight fields were changed,
    // so editing other fields never re-rounds it.
    const weightUnchanged = lbs === initialWeight.lbs && oz === initialWeight.oz && drms === initialWeight.drms;
    const weightKg = weightUnchanged ? c.weight_kg : editWeightLbs / 2.20462;
    const payload = {
      angler_name: anglerName.trim(),
      location,
      beach_location: beachLocation || null,
      is_artificial_lure: isArtificialLure,
      species,
      weight_kg: weightKg,
      weigh_date: weighDate,
      is_junior: isJunior,
      is_female: isFemale,
      is_catch_and_release: isCatchAndRelease,
      in_ray_bass_hunt: inRayBassHunt,
    };
    let { error: err } = await supabase.from("catches").update(payload).eq("id", c.id);
    if (err && /column .* does not exist|does not exist|not found/i.test(err.message)) {
      const fallbackPayload = { ...payload };
      delete fallbackPayload.is_catch_and_release;
      delete fallbackPayload.in_ray_bass_hunt;
      ({ error: err } = await supabase.from("catches").update(fallbackPayload).eq("id", c.id));
    }
    if (err) { setError(err.message); setSaving(false); setShowConfirm(false); return; }
    setSaving(false);
    setEditing(false);
    setShowConfirm(false);
    onSaved();
  }

  const rowBorder = isLast ? "border-0" : "border-b border-white/5";

  if (editing) {
    return (
      <tr className={`${rowBorder} bg-white/5`}>
        <td className="px-4 py-3" colSpan={7}>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[140px]">
                <label className="mb-1 block text-xs text-slate-500">Angler</label>
                <input
                  list="catch-members-list"
                  value={anglerName}
                  onChange={(e) => setAnglerName(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Location</label>
                <div className="flex gap-2 pt-0.5">
                  {["shore", "boat"].map((loc) => (
                    <button key={loc} type="button" onClick={() => setLocation(loc)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize transition ${location === loc ? "bg-cyan-500 text-slate-950" : "border border-white/10 text-slate-400 hover:text-white"}`}>
                      {loc}
                    </button>
                  ))}
                </div>
              </div>
              {location === "shore" && (
                <div className="w-full max-w-xs">
                  <label className="mb-1 block text-xs text-slate-500">Shore location</label>
                  <select value={beachLocation} onChange={(e) => setBeachLocation(e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60">
                    <option value="">None</option>
                    <option value="Praa Sands Beach">Praa Sands Beach</option>
                    <option value="North Coast Beach">North Coast Beach</option>
                  </select>
                </div>
              )}
              <div className="flex-1 min-w-[160px]">
                <label className="mb-1 block text-xs text-slate-500">Species</label>
                <select value={species} onChange={(e) => setSpecies(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60">
                  <option value="">Select…</option>
                  {speciesData.map((r) => <option key={r.species} value={r.species}>{r.species}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="mb-1 block text-xs text-slate-500">lbs</label>
                <input type="number" min="0" value={lbs} onChange={(e) => setLbs(e.target.value)}
                  className="w-20 rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">oz</label>
                <select value={oz} onChange={(e) => setOz(e.target.value)}
                  className="w-16 rounded-lg border border-white/10 bg-slate-900 px-2 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60">
                  {Array.from({ length: 16 }, (_, i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">drms</label>
                <select value={drms} onChange={(e) => setDrms(e.target.value)}
                  className="w-16 rounded-lg border border-white/10 bg-slate-900 px-2 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60">
                  {Array.from({ length: 16 }, (_, i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              {editPct !== null && (
                <span className={`text-sm font-semibold ${editPct >= 100 ? "text-cyan-300" : editPct >= 75 ? "text-emerald-300" : "text-amber-300"}`}>
                  {editPct.toFixed(1)}%
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Weigh Date</label>
                <input type="date" value={weighDate} onChange={(e) => setWeighDate(e.target.value)}
                  className="rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Angler Details</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={isJunior}
                      onChange={(e) => setIsJunior(e.target.checked)}
                      className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
                    />
                    <span className="text-xs text-slate-300">Junior</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={isFemale}
                      onChange={(e) => setIsFemale(e.target.checked)}
                      className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
                    />
                    <span className="text-xs text-slate-300">Female</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={isArtificialLure}
                      onChange={(e) => setIsArtificialLure(e.target.checked)}
                      className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
                    />
                    <span className="text-xs text-slate-300">Artificial lure</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={isCatchAndRelease}
                      onChange={(e) => setIsCatchAndRelease(e.target.checked)}
                      className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
                    />
                    <span className="text-xs text-slate-300">C&amp;R</span>
                  </label>
                  {showRayBassHunt && (
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={inRayBassHunt}
                        onChange={(e) => setInRayBassHunt(e.target.checked)}
                        className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
                      />
                      <span className="text-xs text-slate-300">Ray &amp; Bass Hunt</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <div className="flex gap-2">
              <button onClick={requestSaveEdit} disabled={saving}
                className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-cyan-400 disabled:opacity-50 transition">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={cancelEdit}
                className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-400 hover:text-white transition">
                Cancel
              </button>
            </div>
          </div>
          <ConfirmDialog
            open={showConfirm}
            title="Confirm catch update"
            message={`Update this catch to ${species} weighing ${formatLbs(editWeightLbs)} for ${anglerName.trim()}${isCatchAndRelease ? " as Catch & Release" : " as not Catch & Release"}?`}
            confirmLabel="Confirm & Save"
            loading={saving}
            onConfirm={saveEdit}
            onCancel={() => setShowConfirm(false)}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr className={`${rowBorder} hover:bg-white/5 transition`}>
      <td className="px-4 py-3 font-medium text-white">{c.angler_name}</td>
      <td className="px-4 py-3 text-slate-300">
        <div className="flex flex-col gap-1 items-start">
          <span>{c.species}</span>
          <RecordBadge check={recordCheck} />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className={`rounded px-2 py-0.5 text-xs font-semibold capitalize ${c.location === "boat" ? "bg-sky-400/20 text-sky-300" : "bg-emerald-400/20 text-emerald-300"}`}>
            {c.location}
          </span>
          {c.beach_location && (
            <span className="rounded px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 bg-white/5">
              {c.beach_location}
            </span>
          )}
          {c.is_artificial_lure && (
            <span className="rounded px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.18em] text-cyan-300 bg-cyan-400/10">
              Artificial lure
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatKgLbsOz(c.weight_kg)}</td>
      <td className={`px-4 py-3 font-semibold ${viewPct === null ? "text-slate-500" : viewPct >= 100 ? "text-cyan-300" : viewPct >= 75 ? "text-emerald-300" : "text-amber-300"}`}>
        {viewPct !== null ? `${viewPct.toFixed(1)}%` : "—"}
      </td>
      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{c.weigh_date ? formatDate(c.weigh_date) : "—"}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-1">
          <button onClick={() => setEditing(true)}
            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-cyan-400/10 hover:text-cyan-400"
            title="Edit">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
            </svg>
          </button>
          <button onClick={() => onDelete(c.id)}
            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-red-400/10 hover:text-red-400"
            title="Delete">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CatchLog({ catches, members, onDelete, onSaved, recordIndexes, baseRecordIndexes }) {
  if (catches.length === 0) return <p className="text-sm text-slate-400">No catches logged yet.</p>;
  const recordChecks = evaluateRecordChecks(catches, baseRecordIndexes ?? recordIndexes);
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3">Angler</th>
            <th className="px-4 py-3">Species</th>
            <th className="px-4 py-3">Location</th>
            <th className="px-4 py-3">Weight</th>
            <th className="px-4 py-3">%</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {catches.map((c, i) => (
            <CatchRow key={c.id} c={c} members={members} onDelete={onDelete} onSaved={onSaved} isLast={i === catches.length - 1} recordCheck={recordChecks[c.id]} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Login form ────────────────────────────────────────────────────────────────
function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) setError(err.message);
    else onLogin();
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-8 shadow-2xl shadow-black/60">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Admin Login</h1>
            <p className="text-xs text-slate-400">RSAA Member Management</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-400">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
              placeholder="admin@example.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-400">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p className="rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Add member form ───────────────────────────────────────────────────────────
function AddMemberForm({ members, onAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isJunior, setIsJunior] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const isDuplicate = (members ?? []).some(
      (m) => m.email.trim().toLowerCase() === trimmedEmail && normalizeName(m.name) === normalizeName(trimmedName)
    );
    if (isDuplicate) {
      setError("A member with this name and email already exists.");
      return;
    }
    setLoading(true);
    const { error: err } = await supabase
      .from("members")
      .insert([{ name: trimmedName, email: trimmedEmail, is_junior: isJunior }]);
    if (err) setError(err.message);
    else { setName(""); setEmail(""); setIsJunior(false); onAdded(); }
    setLoading(false);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
      <div className="min-w-[160px] flex-1">
        <label className="mb-1.5 block text-xs font-medium text-slate-400">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="John Smith"
          className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
        />
      </div>
      <div className="min-w-[200px] flex-[2]">
        <label className="mb-1.5 block text-xs font-medium text-slate-400">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="john@example.com"
          className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
        />
      </div>
      <label className="flex items-center gap-2 pb-2.5">
        <input
          type="checkbox"
          checked={isJunior}
          onChange={(e) => setIsJunior(e.target.checked)}
          className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
        />
        <span className="text-sm text-slate-300">Junior</span>
      </label>
      <button
        type="submit"
        disabled={loading}
        className="flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
        {loading ? "Adding…" : "Add Member"}
      </button>
      {error && (
        <p className="w-full rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">
          {error}
        </p>
      )}
    </form>
  );
}

// ── Bulk import ───────────────────────────────────────────────────────────────
function BulkImport({ onImported }) {
  const [text, setText] = useState("");
  const [isJunior, setIsJunior] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const found = [...new Set((text.match(EMAIL_RE) ?? []).map((e) => e.trim().toLowerCase()))];

  async function handleImport() {
    if (!found.length) return;
    setLoading(true);
    setResult(null);
    const rows = found.map((e) => ({ name: e, email: e, is_junior: isJunior }));
    const { error } = await supabase
      .from("members")
      .upsert(rows, { onConflict: "email,name_normalized", ignoreDuplicates: true })
      .select("id", { count: "exact", head: true });
    if (error) setResult({ error: error.message });
    else { setResult({ added: found.length }); setText(""); setIsJunior(false); onImported(); }
    setLoading(false);
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <h2 className="mb-1 text-sm font-semibold text-slate-300">Bulk Import</h2>
      <p className="mb-4 text-xs text-slate-500">Paste any text containing email addresses — they'll be extracted automatically.</p>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setResult(null); }}
        rows={5}
        placeholder="Paste your email list here…"
        className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30 resize-none"
      />
      {found.length > 0 && (
        <p className="mt-2 text-xs text-cyan-400">{found.length} email{found.length !== 1 ? "s" : ""} found</p>
      )}
      <label className="mt-3 flex items-center gap-2">
        <input
          type="checkbox"
          checked={isJunior}
          onChange={(e) => setIsJunior(e.target.checked)}
          className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
        />
        <span className="text-sm text-slate-300">These are junior members</span>
      </label>
      {result?.error && (
        <p className="mt-2 rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">{result.error}</p>
      )}
      {result?.added != null && (
        <p className="mt-2 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-300">
          Imported {result.added} email{result.added !== 1 ? "s" : ""} — duplicates skipped automatically.
        </p>
      )}
      <button
        onClick={handleImport}
        disabled={!found.length || loading}
        className="mt-3 flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:opacity-40"
      >
        <Upload className="h-4 w-4" />
        {loading ? "Importing…" : `Import ${found.length || ""} email${found.length !== 1 ? "s" : ""}`}
      </button>
    </div>
  );
}

// ── Notifications manager ─────────────────────────────────────────────────────
function NotificationsManager() {
  const [notifications, setNotifications] = useState([]);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [message, setMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editLink, setEditLink] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editImageUrl, setEditImageUrl] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

  async function fetchNotifications() {
    const { data } = await supabase
      .from("notifications")
      .select("id, title, link, message, image_url, active, created_at")
      .order("created_at", { ascending: false });
    setNotifications(data ?? []);
  }

  useEffect(() => { fetchNotifications(); }, []);

  async function uploadNotificationImage(file) {
    const ext = file.name.split(".").pop();
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error: uploadErr } = await supabase.storage
      .from("notification-images")
      .upload(path, file);
    if (uploadErr) throw uploadErr;
    const { data } = supabase.storage.from("notification-images").getPublicUrl(path);
    return data.publicUrl;
  }

  function pickImage(file, setFile, setPreview) {
    if (file && file.size > MAX_IMAGE_BYTES) {
      setError("Image must be smaller than 5MB.");
      return;
    }
    setFile(file);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const image_url = imageFile ? await uploadNotificationImage(imageFile) : null;
      const { error: err } = await supabase
        .from("notifications")
        .insert([{
          title: title.trim(),
          link: link.trim() || null,
          message: message.trim(),
          image_url,
          active: true,
        }]);
      if (err) throw err;
      setTitle(""); setLink(""); setMessage("");
      pickImage(null, setImageFile, setImagePreview);
      fetchNotifications();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function toggleActive(n) {
    await supabase.from("notifications").update({ active: !n.active }).eq("id", n.id);
    fetchNotifications();
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this notification?")) return;
    await supabase.from("notifications").delete().eq("id", id);
    fetchNotifications();
  }

  function startEdit(n) {
    setEditingId(n.id);
    setEditTitle(n.title ?? "");
    setEditLink(n.link ?? "");
    setEditMessage(n.message);
    setEditImageUrl(n.image_url ?? null);
    pickImage(null, setEditImageFile, setEditImagePreview);
    setEditError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditTitle("");
    setEditMessage("");
    setEditImageUrl(null);
    pickImage(null, setEditImageFile, setEditImagePreview);
    setEditError(null);
  }

  async function saveEdit(id) {
    if (!editMessage.trim()) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const image_url = editImageFile ? await uploadNotificationImage(editImageFile) : editImageUrl;
      const { error: err } = await supabase
        .from("notifications")
        .update({
          title: editTitle.trim(),
          link: editLink.trim() || null,
          message: editMessage.trim(),
          image_url,
        })
        .eq("id", id);
      if (err) throw err;
      setEditSaving(false);
      setEditingId(null);
      fetchNotifications();
    } catch (err) {
      setEditError(err.message);
      setEditSaving(false);
    }
  }

  return (
    <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="h-5 w-5 text-amber-400" />
        <h2 className="text-sm font-semibold text-slate-300">Notifications</h2>
      </div>

      <form onSubmit={handleAdd} className="space-y-3 mb-6">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-400">Title (optional)</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Competition Update"
            className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-400">Link (optional)</label>
          <input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://example.com"
            className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-400">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={3}
            placeholder="Enter your notification message…"
            className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 resize-none"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-400">Image (optional)</label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img src={imagePreview} alt="" className="max-h-40 rounded-xl border border-white/10" />
              <button
                type="button"
                onClick={() => pickImage(null, setImageFile, setImagePreview)}
                className="absolute -right-2 -top-2 rounded-full bg-slate-950 p-1 text-slate-300 hover:text-white"
                title="Remove image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <label className="flex w-fit cursor-pointer items-center gap-2 rounded-xl border border-dashed border-white/15 bg-slate-900 px-4 py-2.5 text-sm text-slate-400 transition hover:border-amber-400/40 hover:text-white">
              <ImageIcon className="h-4 w-4" />
              Choose image
              <input
                type="file"
                accept="image/*"
                onChange={(e) => pickImage(e.target.files?.[0] || null, setImageFile, setImagePreview)}
                className="hidden"
              />
            </label>
          )}
        </div>
        {error && <p className="rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">{error}</p>}
        <button
          type="submit"
          disabled={!message.trim() || loading}
          className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-amber-400 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {loading ? "Saving…" : "Add Notification"}
        </button>
      </form>

      {notifications.length === 0 ? (
        <p className="text-sm text-slate-400">No notifications yet.</p>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => (
            <div key={n.id} className={`rounded-xl border px-4 py-3 ${n.active ? "border-amber-400/20 bg-amber-400/5" : "border-white/5 bg-white/3 opacity-50"}`}>
              {editingId === n.id ? (
                <div className="space-y-2">
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Title (optional)</label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="e.g. Competition Update"
                      className="w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-amber-400/60"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Link (optional)</label>
                    <input
                      type="url"
                      value={editLink}
                      onChange={(e) => setEditLink(e.target.value)}
                      placeholder="https://example.com"
                      className="w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-amber-400/60"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Message</label>
                    <textarea
                      value={editMessage}
                      onChange={(e) => setEditMessage(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none focus:border-amber-400/60 resize-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Image (optional)</label>
                    {editImagePreview || editImageUrl ? (
                      <div className="relative inline-block">
                        <img src={editImagePreview || editImageUrl} alt="" className="max-h-32 rounded-lg border border-white/10" />
                        <button
                          type="button"
                          onClick={() => { setEditImageUrl(null); pickImage(null, setEditImageFile, setEditImagePreview); }}
                          className="absolute -right-2 -top-2 rounded-full bg-slate-950 p-1 text-slate-300 hover:text-white"
                          title="Remove image"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-slate-900 px-3 py-1.5 text-xs text-slate-400 transition hover:border-amber-400/40 hover:text-white">
                        <ImageIcon className="h-3.5 w-3.5" />
                        Choose image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => pickImage(e.target.files?.[0] || null, setEditImageFile, setEditImagePreview)}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  {editError && <p className="text-xs text-red-400">{editError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(n.id)}
                      disabled={!editMessage.trim() || editSaving}
                      className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50 transition"
                    >
                      {editSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-400 hover:text-white transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    {n.title && <p className="text-xs font-semibold text-amber-300 mb-0.5">{n.title}</p>}
                    {n.image_url && (
                      <img src={n.image_url} alt="" className="mb-2 max-h-32 rounded-lg border border-white/10" />
                    )}
                    <p className="text-sm text-slate-300">{n.message}</p>
                    {n.link && (
                      <a
                        href={n.link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block text-xs font-semibold text-cyan-300 hover:text-white"
                      >
                        {n.link}
                      </a>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      onClick={() => toggleActive(n)}
                      className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition ${n.active ? "bg-emerald-400/20 text-emerald-300 hover:bg-emerald-400/30" : "bg-white/10 text-slate-400 hover:text-white"}`}
                      title={n.active ? "Deactivate" : "Activate"}
                    >
                      {n.active ? "Live" : "Off"}
                    </button>
                    <button
                      onClick={() => startEdit(n)}
                      className="rounded-lg p-1.5 text-slate-500 transition hover:bg-amber-400/10 hover:text-amber-400"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(n.id)}
                      className="rounded-lg p-1.5 text-slate-500 transition hover:bg-red-400/10 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main admin panel ──────────────────────────────────────────────────────────
function AdminPanel() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const COPY_LABELS = { all: "Copy all emails", junior: "Copy junior emails", senior: "Copy senior emails" };
  const [copyLabels, setCopyLabels] = useState(COPY_LABELS);
  const [catches, setCatches] = useState([]);
  const [downloadMonth, setDownloadMonth] = useState(new Date().toISOString().slice(0, 7));
  const [catchLogOpen, setCatchLogOpen] = useState(false);
  const [recordIndexes, setRecordIndexes] = useState(null);
  const [baseRecordIndexes, setBaseRecordIndexes] = useState(null);
  const recordIndexesRef = useRef(null);
  const baseRecordIndexesRef = useRef(null);

  useEffect(() => {
    recordIndexesRef.current = recordIndexes;
  }, [recordIndexes]);

  async function fetchMembers() {
    setLoading(true);
    const { data } = await supabase
      .from("members")
      .select("id, name, email, is_junior")
      .order("name");
    setMembers(data ?? []);
    setLoading(false);
  }

  const fetchCatches = useCallback(async (indexes = null) => {
    const baseColumns = "id, created_at, angler_name, location, beach_location, is_artificial_lure, species, weight_kg, weigh_date, is_junior, is_catch_and_release";
    let { data, error } = await supabase
      .from("catches")
      .select(`${baseColumns}, in_ray_bass_hunt`)
      .order("created_at", { ascending: false });
    if (error) {
      // in_ray_bass_hunt may not exist yet (see scripts/add-ray-bass-hunt-column.sql)
      ({ data } = await supabase
        .from("catches")
        .select(baseColumns)
        .order("created_at", { ascending: false }));
    }
    const nextCatches = data ?? [];
    setCatches(nextCatches);
    const baseIndexes = indexes ?? baseRecordIndexesRef.current ?? (await loadRecordIndexes());
    const nextIndexes = applyRecordUpdatesToIndexes(baseIndexes, nextCatches);
    recordIndexesRef.current = nextIndexes;
    setRecordIndexes(nextIndexes);
  }, []);

  useEffect(() => {
    fetchMembers();
    loadRecordIndexes().then((indexes) => {
      baseRecordIndexesRef.current = indexes;
      recordIndexesRef.current = indexes;
      setBaseRecordIndexes(indexes);
      setRecordIndexes(indexes);
      fetchCatches(indexes);
    });
  }, [fetchCatches]);

  async function deleteMember(id) {
    if (!window.confirm("Remove this member?")) return;
    await supabase.from("members").delete().eq("id", id);
    fetchMembers();
  }

  async function toggleJunior(id, current) {
    await supabase.from("members").update({ is_junior: !current }).eq("id", id);
    fetchMembers();
  }

  async function deleteCatch(id) {
    if (!window.confirm("Delete this catch?")) return;
    await supabase.from("catches").delete().eq("id", id);
    fetchCatches();
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  function copyEmails(filter) {
    const list = filter === "junior" ? members.filter((m) => m.is_junior)
      : filter === "senior" ? members.filter((m) => !m.is_junior)
      : members;
    const emails = list.map((m) => m.email).join("; ");
    navigator.clipboard.writeText(emails);
    setCopyLabels((prev) => ({ ...prev, [filter]: "Copied!" }));
    setTimeout(() => setCopyLabels((prev) => ({ ...prev, [filter]: COPY_LABELS[filter] })), 2500);
  }


  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto max-w-4xl px-6 py-12 sm:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Member Emails</h1>
              <p className="text-xs text-slate-400">
                {members.length} member{members.length !== 1 ? "s" : ""}
                {" "}({members.filter((m) => m.is_junior).length} junior, {members.filter((m) => !m.is_junior).length} senior)
              </p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-400 transition hover:border-white/20 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>

        {/* Action buttons */}
        {members.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-3">
            <button
              onClick={() => copyEmails("all")}
              className="flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2.5 text-sm font-medium text-cyan-300 transition hover:bg-cyan-400/20"
            >
              <Copy className="h-4 w-4" />
              {copyLabels.all}
            </button>
            <button
              onClick={() => copyEmails("junior")}
              className="flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2.5 text-sm font-medium text-cyan-300 transition hover:bg-cyan-400/20"
            >
              <Copy className="h-4 w-4" />
              {copyLabels.junior}
            </button>
            <button
              onClick={() => copyEmails("senior")}
              className="flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2.5 text-sm font-medium text-cyan-300 transition hover:bg-cyan-400/20"
            >
              <Copy className="h-4 w-4" />
              {copyLabels.senior}
            </button>
          </div>
        )}

        {/* Bulk import */}
        <div className="mb-6">
          <BulkImport onImported={fetchMembers} />
        </div>

        {/* Add single member */}
        <div className="mb-8 rounded-2xl border border-white/10 bg-white/5 p-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-300">Add Single Member</h2>
          <AddMemberForm members={members} onAdded={fetchMembers} />
        </div>

        {/* Notifications */}
        <NotificationsManager />

        {/* Log fish catch */}
        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Fish className="h-5 w-5 text-cyan-300" />
            <h2 className="text-sm font-semibold text-slate-300">Log Fish Catch</h2>
          </div>
          <AddCatchForm members={members} onAdded={() => fetchCatches()} recordIndexes={recordIndexes} />
        </div>

        {/* Catch log */}
        <div className="mb-8 rounded-2xl border border-white/10 bg-white/5 p-6">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-300">Catch Log ({catches.length})</h2>
            <div className="flex items-end gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Download by month</label>
                <div className="flex items-center gap-2">
                  <input
                    type="month"
                    value={downloadMonth}
                    onChange={(e) => setDownloadMonth(e.target.value)}
                    className="rounded-xl border border-white/10 bg-slate-900 px-3 py-1.5 text-xs text-white outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30"
                  />
                  <button
                    onClick={() => downloadCatchesForMonthCsv(catches, downloadMonth)}
                    disabled={!downloadMonth}
                    className="flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-3 py-1.5 text-xs font-medium text-cyan-300 transition hover:bg-cyan-400/20 disabled:opacity-40"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download (CSV)
                  </button>
                </div>
              </div>
              <button
                onClick={() => setCatchLogOpen((open) => !open)}
                className="flex items-center gap-1.5 rounded-full border border-cyan-400/40 px-4 py-1.5 text-sm font-medium text-cyan-300 transition hover:bg-cyan-400/10"
              >
                {catchLogOpen ? "Hide" : "Show"}
                {catchLogOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {catchLogOpen && (
            <CatchLog catches={catches} members={members} onDelete={deleteCatch} onSaved={() => fetchCatches()} recordIndexes={recordIndexes} baseRecordIndexes={baseRecordIndexes} />
          )}
        </div>

        {/* Member list */}
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
          {loading ? (
            <p className="p-6 text-center text-sm text-slate-400">Loading…</p>
          ) : members.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-400">No members yet — add one above.</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Email</th>
                  <th className="px-6 py-3 text-center">Junior</th>
                  <th className="px-6 py-3 text-right">Remove</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m, i) => (
                  <tr
                    key={m.id}
                    className={`border-b border-white/5 transition hover:bg-white/5 ${i === members.length - 1 ? "border-0" : ""}`}
                  >
                    <td className="px-6 py-3 font-medium text-white">{m.name}</td>
                    <td className="px-6 py-3 text-slate-300">{m.email}</td>
                    <td className="px-6 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={!!m.is_junior}
                        onChange={() => toggleJunior(m.id, m.is_junior)}
                        title="Junior member"
                        className="rounded border-white/20 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30"
                      />
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => deleteMember(m.id)}
                        className="rounded-lg p-1.5 text-slate-500 transition hover:bg-red-400/10 hover:text-red-400"
                        title="Remove member"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [session, setSession] = useState(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  return (
    <>
      <PageMeta title="Admin | Redruth Sea Angling Association" noindex />
      {session === undefined ? null : !session ? <LoginForm onLogin={() => {}} /> : <AdminPanel />}
    </>
  );
}
